import { StyleSheet } from "react-native";

export const colors = {
  primary: "#14532D",
  primaryHover: "#166534",
  primaryLight: "#F0FDF4",
  primaryBorder: "#BBF7D0",
  
  accent: "#0F766E",
  accentLight: "#F0FDFA",

  bg: "#F8FAFC",
  surface: "#FFFFFF",
  border: "#E4E7EC",
  borderDark: "#D0D5DD",

  text: "#111827",
  muted: "#667085",
  subtle: "#98A2B3",

  danger: "#DC2626",
  dangerHover: "#B91C1C",
  dangerLight: "#FEF2F2",
  dangerBorder: "#FCA5A5",

  warning: "#D97706",
  warningLight: "#FFFBEB",
  warningBorder: "#FDE68A",

  info: "#0284C7",
  infoLight: "#F0F9FF",
  infoBorder: "#BAE6FD",

  secondaryBg: "#F1F5F9",
  secondaryHover: "#E2E8F0",

  // Legacy mappings for backwards compatibility
  ink: "#111827",
  line: "#E4E7EC",
  page: "#F8FAFC",
  primarySoft: "#F0FDF4",
  warn: "#D97706"
};

export const shadows = {
  subtle: {
    shadowColor: "#101828",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 2,
    elevation: 1
  },
  card: {
    shadowColor: "#101828",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2
  },
  dropdown: {
    shadowColor: "#101828",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 10,
    elevation: 5
  }
};

export const styles = StyleSheet.create({
  app: {
    flex: 1,
    width: "100%",
    height: "100%",
    backgroundColor: colors.bg,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
  },
  page: {
    flex: 1,
    padding: 18,
    gap: 14,
    width: "100%",
    maxWidth: 1600,
    alignSelf: "center"
  },
  row: {
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-end",
    flexWrap: "wrap"
  },
  rowCenter: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap"
  },
  between: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap"
  },
  panel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    borderRadius: 10,
    padding: 14,
    gap: 12,
    ...shadows.subtle
  },
  card: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    borderRadius: 10,
    padding: 14,
    gap: 12,
    ...shadows.subtle
  },
  title: {
    fontSize: 24,
    fontWeight: "700",
    color: colors.text,
    letterSpacing: -0.4
  },
  h2: {
    fontSize: 17,
    fontWeight: "700",
    color: colors.text,
    letterSpacing: -0.2
  },
  h3: {
    fontSize: 15,
    fontWeight: "600",
    color: colors.text
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.text,
    marginBottom: 4
  },
  body: {
    fontSize: 13,
    fontWeight: "400",
    color: colors.text,
    lineHeight: 18
  },
  small: {
    fontSize: 12,
    fontWeight: "400",
    color: colors.muted
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 13,
    color: colors.text,
    backgroundColor: colors.surface,
    minWidth: 180
  },
  textArea: {
    height: "auto",
    minHeight: 80,
    textAlignVertical: "top",
    paddingTop: 8
  },
  button: {
    height: 38,
    borderRadius: 8,
    paddingHorizontal: 14,
    backgroundColor: colors.primary,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    cursor: "pointer",
    flexShrink: 0
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "600",
    whiteSpace: "nowrap"
  },
  secondaryButton: {
    backgroundColor: colors.secondaryBg,
    borderWidth: 1,
    borderColor: colors.border
  },
  secondaryText: {
    color: colors.text,
    fontWeight: "600"
  },
  dangerButton: {
    backgroundColor: colors.danger
  },
  tableHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.secondaryBg,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomColor: colors.border,
    borderBottomWidth: 1,
    alignItems: "center",
    backgroundColor: colors.surface,
    minHeight: 52
  },
  tableRowAlt: {
    backgroundColor: colors.bg
  },
  cell: {
    fontSize: 13,
    color: colors.text,
    lineHeight: 18
  },
  cellHeader: {
    fontSize: 11,
    fontWeight: "700",
    color: colors.muted,
    textTransform: "uppercase",
    letterSpacing: 0.4
  },
  termList: {
    gap: 8,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: 12,
    backgroundColor: colors.surface
  },
  logoPanel: {
    gap: 12,
    alignItems: "flex-start"
  },
  checkRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 2
  },
  checkbox: {
    width: 18,
    height: 18,
    borderWidth: 1,
    borderColor: colors.borderDark,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.surface
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  checkLabel: {
    flex: 1,
    color: colors.text,
    fontSize: 13,
    lineHeight: 18
  },
  message: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 13,
    fontWeight: "500"
  },
  errorMessage: {
    backgroundColor: colors.dangerLight,
    borderColor: colors.dangerBorder,
    color: colors.danger
  },
  successMessage: {
    backgroundColor: colors.primaryLight,
    borderColor: colors.primaryBorder,
    color: colors.primary
  },
  stat: {
    flex: 1,
    minWidth: 180,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: 14,
    gap: 4,
    ...shadows.subtle
  },
  statNumber: {
    fontSize: 20,
    fontWeight: "700",
    color: colors.primary,
    letterSpacing: -0.3,
    lineHeight: 26
  },
  badge: {
    backgroundColor: colors.primaryLight,
    color: colors.primary,
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 14,
    fontSize: 11,
    fontWeight: "600",
    overflow: "hidden"
  }
});
